import { sendSentryError } from '@lib/sentry';
import { CONTENT_CATEGORY_ID } from '@/constants/content-categoryId';
import { fetchAllFromCache, fetchFromCache, FetchAPIOptions } from './api';
import {
  fetchAllFromWordpressAndCache,
  fetchFromWordpressAndCache,
} from './wordpress-api';
import { FAQ_LIST_QUERY, FAQ_ITEM_QUERY_BY_SLUG } from './wordpress-queries';

// ============================================
// Types
// ============================================

/**
 * 리스트용 경량 타입 (목록/검색 페이지에서 사용, content 없음)
 */
export interface FaqListItemProp {
  id: string;
  title: string;
  slug: string;
  lang: string[];
  category: string[];
}

/**
 * 상세 페이지용 전체 타입 (개별 FAQ 페이지에서 사용, content 포함)
 */
export interface FaqProp extends FaqListItemProp {
  content: string;
}

// ============================================
// List Fetch Functions (경량 데이터)
// ============================================

/**
 * WordPress에서 FAQ 리스트를 가져와 S3에 캐시 (refresh용)
 */
const fetchFaqItemsFromWordpress = async (
  label: string,
  prodFilter?: boolean,
) => {
  return fetchAllFromWordpressAndCache(
    {
      query: FAQ_LIST_QUERY,
      getVariables: cursor => ({
        categoryId: CONTENT_CATEGORY_ID.faq,
        postCount: 100,
        after: cursor,
      }),
      getPageInfo: data => ({
        hasNextPage: data?.posts?.pageInfo?.hasNextPage || false,
        endCursor: data?.posts?.pageInfo?.endCursor || null,
      }),
      getItems: data => data?.posts?.edges || [],
    },
    { label, prodFilter },
  );
};

/**
 * S3 캐시에서 FAQ 리스트를 가져옴 (빌드/런타임용)
 */
const fetchFaqItemsFromCache = async (label: string) => {
  return fetchAllFromCache(
    {
      getPageInfo: data => ({
        hasNextPage: data?.posts?.pageInfo?.hasNextPage || false,
      }),
      getItems: data => data?.posts?.edges || [],
      postCount: 100,
    },
    { label },
  );
};

/**
 * FAQ 리스트용 경량 데이터 조회 (content 제외)
 * - 목록/검색 페이지에서 사용
 */
export async function getFaqItemsForList(
  options?: FetchAPIOptions,
): Promise<FaqListItemProp[]> {
  try {
    const label = 'getFaqItemsForList';
    const refresh = options?.refresh ?? false;

    const data = refresh
      ? await fetchFaqItemsFromWordpress(label, options?.prodFilter)
      : await fetchFaqItemsFromCache(label);

    return (
      data.map((edge: any) => ({
        id: edge.node.id,
        title: edge.node.title,
        slug: edge.node.slug,
        lang: edge.node.faq?.lang ?? [],
        category: edge.node.faq?.category ?? [],
      })) || []
    );
  } catch (error) {
    sendSentryError({
      error,
      source: 'faq-get-list',
    });
    return [];
  }
}

// ============================================
// Individual Item Fetch Functions (개별 상세 데이터)
// ============================================

/**
 * 개별 FAQ 아이템 상세 조회 (slug 기반)
 * - 상세 페이지에서 사용
 * - 개별 캐시 적용
 */
export async function getFaqItemBySlug(
  slug: string,
  options?: FetchAPIOptions,
): Promise<FaqProp | null> {
  try {
    const label = `getFaqItem-${slug}`;
    const refresh = options?.refresh ?? false;

    let data: any;

    if (refresh) {
      data = await fetchFromWordpressAndCache(
        FAQ_ITEM_QUERY_BY_SLUG,
        {
          variables: {
            slug,
            categoryId: CONTENT_CATEGORY_ID.faq,
          },
        },
        { label, prodFilter: options?.prodFilter },
      );
    } else {
      data = await fetchFromCache(label);
    }

    // 카테고리 스코프 쿼리는 posts.nodes[0] 로 반환. 구 캐시(post) 하위호환.
    const post = data?.posts?.nodes?.[0] ?? data?.post;
    if (!post) return null;
    return {
      id: post.id,
      title: post.title,
      slug: post.slug,
      content: post.content,
      lang: post.faq?.lang ?? [],
      category: post.faq?.category ?? [],
    };
  } catch (error) {
    sendSentryError({
      error,
      extra: { slug },
      source: 'faq-get-item-by-slug',
    });
    return null;
  }
}
